/**
 * The feedback page's pages with their notes (#648, #661): the white page on the warm ground, a rounded frame around each block, and a
 * note gutter beside it, all one zoomable layer; the summary chips above it.
 *
 * Every page that has notes is rendered (its image, frames and notes) and only the current one is shown: the others are hidden by
 * the stylesheet, so stepping and the thumbnails are presentation over the same sections. Stepping goes between the pages that
 * have notes. Nothing here asks for or records help; the notes are the caller's (`renderNote`).
 *
 * Picking a note (its title) highlights its frame, and picking a frame (its mark, or a tap on it) highlights its note and brings the
 * focus to it. On a phone the same notes are a stack, block by block (`feedback-phone.tsx`).
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { useIsPhone } from '../../app/use-is-phone.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { useFeedbackLabels } from '../../i18n/homework-feedback-labels.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { AlsoToLook, needsALook } from './feedback-also.js';
import { type BoardNote } from './feedback-blocks.js';
import { feedbackChrome, markFamily } from './feedback-chrome.js';
import { PageFrames, QuestionBand } from './feedback-frames.js';
import { noteDomId } from './feedback-note.js';
import { NotesLayer } from './feedback-notes-layer.js';
import { FeedbackPageImage } from './feedback-page-image.js';
import { PhoneFeedback } from './feedback-phone.js';
import type { FeedbackNote } from './feedback-model.js';
import { PageViewer } from './feedback-viewer.js';

/**
 * The chips at the end of the top row: one per kind of note that is present, with no number. The founder struck the
 * count line (UX_EXPERIENCE §4.6, #438: "a line of counts reads as a score even without an 'of'"); the chips keep the
 * canvas's look without it.
 */
export function SummaryChips({ notes, onlyNotCheckable = false, lookHere = false }: { readonly notes: readonly FeedbackNote[]; readonly onlyNotCheckable?: boolean; readonly lookHere?: boolean }): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const count = { checked: 0, look: 0, review: 0, not_checkable: 0 };
  for (const note of notes) {
    const family = markFamily(note.mark);
    if (family !== null) count[family] += 1;
  }
  // The question sheet is unavailable and nothing was decided: the one chip says so. When a ✓ or ✗ exists the chips stay truthful and normal (#661):
  // the failure is the second reader's, not the kernel's, so the decided notes still stand.
  if (onlyNotCheckable) {
    count.checked = 0;
    count.look = 0;
    count.review = 0;
    count.not_checkable = 1;
  }
  // Presence only: the numbers above decide which chips show and are never drawn.
  return (
    <ul className="fb-chips plain-list" aria-label={m.summaryList}>
      {count.checked > 0 ? <li className="fb-chip" data-family="checked">{m.chipChecked}</li> : null}
      {count.look > 0 ? <li className="fb-chip" data-family="look">{m.chipLook}</li> : null}
      {lookHere && !onlyNotCheckable ? <li className="fb-chip" data-family="pointer">{m.chipPointer}</li> : null}
      {count.review > 0 ? <li className="fb-chip" data-family="review">{m.chipReview}</li> : null}
      {count.not_checkable > 0 ? <li className="fb-chip" data-family="not_checkable">{m.chipNotCheckable}</li> : null}
    </ul>
  );
}

export interface NoteContext {
  readonly selected: boolean;
  readonly onSelect: (id: string) => void;
  /** The note is drawn as a sheet over the page (the phone), not as a card in the gutter. */
  readonly bare?: boolean;
  /** Draw only the way to this line's evidence, with no card: a line that was only read, in an opened run on a phone. */
  readonly howOnly?: boolean;
}

export interface FeedbackPagesProps {
  readonly pageIds: readonly string[];
  readonly totalPages: number;
  readonly pageNumberOf: (pageId: string) => number;
  readonly pageOf: (pageId: string) => { readonly widthPx: number; readonly heightPx: number } | undefined;
  /** Every note of the sheet, in reading order (`buildBoardNotes`). */
  readonly notes: readonly BoardNote[];
  readonly renderNote: (entry: BoardNote, context: NoteContext) => React.JSX.Element;
  /** "Post a fixed version" is offered only on a re-check: an earlier check of the same exercise exists. */
  readonly showPostFixed: boolean;
  /** Receives the element at the top of the gutter, where a card that belongs there (the question sheet is missing) is drawn. */
  readonly leadRef?: (element: HTMLDivElement | null) => void;
}

export function FeedbackPages(props: FeedbackPagesProps): React.JSX.Element {
  const phone = useIsPhone();
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [selectedId, setSelected] = useState<string | undefined>(undefined);
  const select = (id: string, focus = false): void => {
    setSelected((now) => (now === id && !focus ? undefined : id));
    if (!focus) return;
    // A frame's mark names its note and brings the focus to it, so the keyboard follows the page to the words.
    const title = document.getElementById(noteDomId(id))?.querySelector<HTMLElement>('.fb-note-title');
    title?.focus({ preventScroll: false });
  };
  // Opens on the first page with a note that needs a look, or page 1 when none has one (presentation only).
  const firstLook = props.pageIds.find((id) => props.notes.some((entry) => entry.pageId === id && needsALook(entry)));
  const current = picked !== undefined && props.pageIds.includes(picked) ? picked : (firstLook ?? props.pageIds[0]);
  if (phone) return <PhoneFeedback {...props} selectedId={selectedId} onSelect={select} />;
  return <DesktopPages {...props} current={current} onGo={setPicked} selectedId={selectedId} onSelect={select} />;
}

function DesktopPages(props: FeedbackPagesProps & { readonly current: string | undefined; readonly onGo: (pageId: string) => void; readonly selectedId: string | undefined; readonly onSelect: (id: string, focus?: boolean) => void }): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const t = useFeedbackLabels();
  const h = useHomeworkLabels();
  const { locale } = useLocale();
  const rtl = locale === 'he';
  const at = props.current === undefined ? -1 : props.pageIds.indexOf(props.current);
  const thumbs = props.pageIds.map((id) => ({ id, number: props.pageNumberOf(id) }));
  return (
    <div className="fb-pages">
      {props.pageIds.map((pageId) => {
        const page = props.pageOf(pageId);
        const isCurrent = pageId === props.current;
        const entries = props.notes.filter((entry) => entry.pageId === pageId);
        const pageNumber = props.pageNumberOf(pageId);
        return (
          <section key={pageId} className="fb-page" data-current={isCurrent} aria-labelledby={`feedback-page-${pageId}`}>
            <h2 id={`feedback-page-${pageId}`} className="visually-hidden">
              {h.pageN(pageNumber)}
            </h2>
            {isCurrent ? (
              // Above the viewer, outside the zoom layer: the band does not scale or move with pan, and as wide as the page (not the notes column).
              <div className="fb-qband-fit" style={{ ['--fb-w' as string]: page?.widthPx ?? 1, ['--fb-h' as string]: page?.heightPx ?? 1 }}>
                <QuestionBand notes={entries} />
              </div>
            ) : null}
            {isCurrent ? <AlsoToLook notes={props.notes} pageIds={props.pageIds} current={props.current} pageNumberOf={props.pageNumberOf} onGo={props.onGo} /> : null}
            <PageViewer
              current={isCurrent}
              pageId={pageId}
              pageNumber={pageNumber}
              totalPages={props.totalPages}
              thumbs={thumbs}
              previousId={props.pageIds[at - 1]}
              nextId={props.pageIds[at + 1]}
              onGo={props.onGo}
              widthPx={page?.widthPx ?? 1}
              heightPx={page?.heightPx ?? 1}
              onTap={(id) => props.onSelect(id, true)}
              gutter={(box) => (
                <NotesLayer
                  box={box}
                  widthPx={page?.widthPx ?? 1}
                  notes={entries}
                  rtl={rtl}
                  selectedId={props.selectedId}
                  renderNote={(entry) => props.renderNote(entry, { selected: entry.id === props.selectedId, onSelect: (id) => props.onSelect(id) })}
                  {...(isCurrent && props.leadRef !== undefined ? { lead: <div ref={props.leadRef} className="fb-lead" /> } : {})}
                />
              )}
            >
              {page !== undefined ? (
                <>
                  <FeedbackPageImage pageId={pageId} pageNumber={pageNumber} widthPx={page.widthPx} heightPx={page.heightPx} />
                  <PageFrames
                    widthPx={page.widthPx}
                    heightPx={page.heightPx}
                    notes={entries}
                    selectedId={props.selectedId}
                    onSelect={(id) => props.onSelect(id, true)}
                  />
                </>
              ) : null}
            </PageViewer>
            {isCurrent && entries.some((entry) => entry.blockBbox !== null) ? <p className="fb-frame-words">{t.frameWords}</p> : null}
            {isCurrent && props.showPostFixed ? (
              <Link className="fb-post-fixed" to="/homework">
                {m.postFixed}
              </Link>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
